import classNames from 'classnames';
import { Button, ButtonSizeXL } from '@picsart/button';
import { useEffect, useState } from 'react';
import { Form, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { IValues } from '../../../utils/common/types';
import { handleScroll } from '../../../utils/common/scroll';
import useFormFieldsByCategory from '../../../utils/hooks/useFormFieldsByCategory';
import useStore from '../../../services/store';
import { ReactComponent as AdvanceDetailsUpArrowIcon } from '../../../svgIcons/advanced-details-up-arrow.svg';
import { ReactComponent as AdvanceDetailsDownArrowIcon } from '../../../svgIcons/advanced-details-down-arrow.svg';
import Choices from '../Choices';

import { IFormContent } from './types';
import useStyles from './styles';
import RenderFormField from './RenderFormField';

function FormContent({ selectedCategory, generateButtonKey }: IFormContent) {
  const { t } = useTranslation();
  const setFormValues = useStore((state) => state.setFormValues);
  const loading = useStore((state) => state.resultsAreGenerating);
  const setResultsAreGenerating = useStore((state) => state.setResultsAreGenerating);
  const showValidationError = useStore((state) => state.showValidationError);
  const [showAdvancedDetails, setShowAdvancedDetails] = useState(false);
  const categoryFields = useFormFieldsByCategory(selectedCategory);
  const props = useFormikContext<IValues>();
  const { isSubmitting, isValid, setTouched, values } = props;
  const classes = useStyles({
    loading,
  });

  useEffect(() => {
    return () => {
      setResultsAreGenerating(false);
    };
  }, []);

  useEffect(() => {
    setFormValues(values);
  }, [values]);

  useEffect(() => {
    if (!isSubmitting) {
      return;
    }

    if (isValid) {
      showValidationError(false);
    } else {
      handleScroll('qt_main_content');
      showValidationError(true);
      setTimeout(() => {
        showValidationError(false);
      }, 6000);
    }
  }, [isSubmitting, isValid]);

  useEffect(() => {
    setTouched({}, false);
  }, [selectedCategory, setTouched]);

  const basicFields = categoryFields.filter(
    ({ properties: { isAdvanced }, type }) => !isAdvanced && type !== 'pills',
  );

  const advancedFields = categoryFields.filter(
    ({ properties: { isAdvanced }, type }) => isAdvanced && type !== 'pills',
  );
  const hasAdvancedDetails = advancedFields.length > 0;
  const pills = categoryFields.filter(({ type }) => type === 'pills')[0];

  return (
    <Form className={classes.root} onSubmit={props.handleSubmit}>
      {pills && (
        <Choices
          pills={pills.properties.pillsValues}
          formProps={props}
          webLabel={pills.properties.textLabel}
          mobileLabel={pills.properties.mobileTextLabel}
          name={pills.properties.name}
        />
      )}

      <div className={classes.fieldsContainer} id="qt_copywriter_fields_container">
        {basicFields.map((field) => (
          <div style={{ flex: field.properties.flex }} key={field.properties.name}>
            <RenderFormField field={field} props={props} />
          </div>
        ))}

        {hasAdvancedDetails && (
          <div
            className={classes.advancedDetails}
            onClick={() => setShowAdvancedDetails((prev) => !prev)}
          >
            {showAdvancedDetails ? (
              <AdvanceDetailsUpArrowIcon className={classes.advancedDetailsArrow} />
            ) : (
              <AdvanceDetailsDownArrowIcon className={classes.advancedDetailsArrow} />
            )}
            {t('editor.advancedDetails')}
          </div>
        )}

        {showAdvancedDetails && (
          <>
            {advancedFields.map((field) => (
              <div style={{ flex: field.properties.flex }} key={field.properties.name}>
                <RenderFormField field={field} props={props} />
              </div>
            ))}
          </>
        )}
      </div>

      <div
        className={classNames(
          classes.buttonContainer,
          hasAdvancedDetails && classes.buttonContainerWithDetails,
        )}
      >
        <Button
          isFullWidth
          type="submit"
          isLoading={loading}
          size={ButtonSizeXL}
          isDisabled={loading}
        >
          {t(generateButtonKey || 'editor.generateButton')}
        </Button>
      </div>
    </Form>
  );
}
export default FormContent;
